import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import Navbar from "../component/Header.js/Navbar/Navbar";
import Footer from "../component/Footer/Footer";
import { CalendarMonthOutlined, Person, ArrowForward } from "@mui/icons-material";

/* ── Styled ──────────────────────────────────────────── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainBox = styled.main`
  flex: 1;
  width: 90%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 0 5rem;
  @media only screen and (max-width: 1099px) {
    width: 95%;
    padding: 2rem 1rem 4rem;
  }
`;

const Breadcrumb = styled.div`
  font-size: 1.2rem;
  color: #888;
  margin-bottom: 1rem;
  a { color: #888; text-decoration: none; &:hover { color: black; } }
  span { margin: 0 0.4rem; }
`;

const PageTitle = styled.h1`
  font-size: 5.7rem;
  font-weight: 400;
  margin: 2rem 0 3rem;
  color: black;
  @media only screen and (max-width: 1099px) {
    font-size: 3.5rem;
    margin: 1.5rem 0 2rem;
  }
  @media only screen and (max-width: 600px) {
    font-size: 2.8rem;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  @media only screen and (max-width: 1099px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  @media only screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const BlogCard = styled.article`
  background: white;
  border-radius: 0.6rem;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 28px rgba(0,0,0,0.13);
  }
`;

const CardImageBox = styled.div`
  width: 100%;
  height: 20rem;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    display: block;
  }
  ${BlogCard}:hover & img {
    transform: scale(1.05);
  }
  @media only screen and (max-width: 600px) {
    height: 22rem;
  }
`;

const CardBody = styled.div`
  padding: 1.6rem;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.2rem;
  color: #888;
  svg { font-size: 1.4rem; }
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: black;
  margin: 0 0 0.8rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 1.3rem;
  color: #511d00;
  font-weight: 500;
  letter-spacing: 0.03rem;
  svg { font-size: 1.3rem; transition: transform 0.2s; }
  ${BlogCard}:hover & svg { transform: translateX(4px); }
`;

/* ── Skeleton ── */
const shimmer = keyframes`
  100% { background-position: right -200px top 0; }
`;
const SkeletonCard = styled.div`
  border-radius: 0.6rem;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
`;
const SkeletonImg = styled.div`
  width: 100%;
  height: 20rem;
  background: linear-gradient(90deg, #ebebeb 0px, #f5f5f5 100px, #ebebeb 200px) left -200px top 0 / 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;
const SkeletonLine = styled.div`
  height: ${(p) => p.h || "1.4rem"};
  width: ${(p) => p.w || "100%"};
  border-radius: 0.3rem;
  margin: ${(p) => p.mt || "0.8rem"} 0 0;
  background: linear-gradient(90deg, #ebebeb 0px, #f5f5f5 100px, #ebebeb 200px) left -200px top 0 / 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;
const SkeletonBody = styled.div`padding: 1.6rem;`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 6rem 2rem;
  color: #bbb;
  font-size: 1.8rem;
  letter-spacing: 0.1rem;
`;

/* ── helpers ── */
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });

/* ── Component ───────────────────────────────────────── */
const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlogs = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/blog/published`);
        const data = await res.json();
        if (data.success) setBlogs(data.data);
      } catch (e) {
        console.error("Blog fetch error:", e);
      }
      setLoading(false);
    };
    fetchBlogs();
  }, []);

  return (
    <PageWrapper>
      <Navbar />
      <MainBox id="blogsDiv">
        <Breadcrumb>
          <Link to="/">Home</Link>
          <span>/</span>
          Blogs
        </Breadcrumb>
        <PageTitle data-aos="fade-right">Blogs</PageTitle>

        <BlogGrid>
          {loading &&
            Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} data-aos="fade-up">
                <SkeletonImg />
                <SkeletonBody>
                  <SkeletonLine h="1.1rem" w="60%" mt="0" />
                  <SkeletonLine h="1.8rem" w="90%" mt="1rem" />
                  <SkeletonLine h="1.8rem" w="70%" mt="0.4rem" />
                  <SkeletonLine h="1.2rem" w="30%" mt="1rem" />
                </SkeletonBody>
              </SkeletonCard>
            ))}

          {!loading && blogs.length === 0 && (
            <EmptyState>No blogs found.</EmptyState>
          )}

          {!loading &&
            blogs.map((blog) => (
              <Link
                key={blog._id}
                to={`/blogs/${blog.slug}`}
                style={{ textDecoration: "none" }}
              >
                <BlogCard data-aos="fade-up">
                  <CardImageBox>
                    <img src={blog.image} alt={blog.title} />
                  </CardImageBox>
                  <CardBody>
                    <MetaRow>
                      <MetaItem>
                        <CalendarMonthOutlined />
                        {fmtDate(blog.createdAt)}
                      </MetaItem>
                      <MetaItem>
                        <Person />
                        {blog.author}
                      </MetaItem>
                    </MetaRow>
                    <CardTitle>{blog.title}</CardTitle>
                    <ReadMore>
                      Read More <ArrowForward />
                    </ReadMore>
                  </CardBody>
                </BlogCard>
              </Link>
            ))}
        </BlogGrid>
      </MainBox>
      <Footer />
    </PageWrapper>
  );
};

export default Blogs;
